"use client";

import { motion } from "framer-motion";

/**
 * HeroV3Heading — the oversized H1 with a staged entrance animation.
 *
 * Three lines reveal sequentially: opacity 0 → 1, slight y translation
 * (12 → 0), and a tiny Gaussian blur clearing (6 → 0) so the text feels
 * like it's materializing rather than popping in. Stagger is 130ms —
 * fast enough that the whole headline finishes under 600ms (below the
 * "feels slow" threshold) but slow enough that the eye reads each line.
 *
 * The gradient accent sits on "execution layer" — one deliberate pop of
 * brand colour in an otherwise editorial headline. Gradient animates in
 * via the same reveal, no extra tricks.
 *
 * Accessibility: on `prefers-reduced-motion` the animation collapses to
 * an opacity fade with zero translation/blur, still staged so there's a
 * subtle rhythm without vestibular discomfort.
 */
export default function HeroV3Heading() {
  const container = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.13, delayChildren: 0.05 } },
  };

  const line = {
    hidden: {
      opacity: 0,
      y: 12,
      filter: "blur(6px)",
    },
    visible: {
      opacity: 1,
      y: 0,
      filter: "blur(0px)",
      transition: { duration: 0.75, ease: [0.2, 0.65, 0.3, 1] as [number, number, number, number] },
    },
  };

  return (
    <motion.h1
      // Fluid font size — scales with viewport HEIGHT primarily so the
      // whole hero fits above the fold (down to "Backed by DexTools")
      // on shorter desktop monitors (1366x768, 1280x720) without scrolling.
      // Cap at 84px to prevent the H1 alone from eating ~290px on tall
      // screens, leaving room for everything below it.
      className="font-inter-tight font-semibold text-gray-900 dark:text-white tracking-[-0.03em] leading-[0.98] text-[40px] sm:text-[clamp(40px,calc(1vw+6vh),84px)]"
      variants={container}
      initial="hidden"
      animate="visible"
    >
      <motion.span variants={line} className="block">
        Copy the best
      </motion.span>
      <motion.span
        variants={line}
        className="block bg-gradient-to-r from-[#CCFF00] via-[#B8EE00] to-[#7CD300] bg-clip-text text-transparent"
      >
        Polymarket traders
      </motion.span>
      <motion.span variants={line} className="block text-gray-400 dark:text-gray-500">
        Same entry.
      </motion.span>
    </motion.h1>
  );
}
